
import { instance as axios, createApiCall } from 'services';

import { call, fork, put, takeLatest, select } from 'redux-saga/effects';

import { Actions, ActionScanSessionHandler, IValidationStartScanSessionCallbackParams, SCAN_SESSION_ID_RESPONSE_FIELD, } from './types';
import { ActionCallbackOnly } from 'store/model';

const activeEventSources: Record<string, EventSource> = {};


function* startScanSessionSaga(): Generator {
  yield takeLatest(Actions.START_SCAN_SESSION, function* (action: ActionScanSessionHandler) {
    try {
      const { scanSessionMessageCallback, startScanSessionCallback, productId, purpose } = action;
      const { data, status } = yield call(createApiCall, {
        method: 'POST',
        url: '/admins/admin/toggle-scan-session',
        data: {
          turnOn: true,
          productId,
          // VRG-265 — undefined is fine: the API defaults to 'certificate'.
          purpose,
        },
      })

      const scanSessionId = data[SCAN_SESSION_ID_RESPONSE_FIELD];

      if (status === 201) {
        yield put({
          type: Actions.SET_SCAN_SESSION_ID,
          // The purpose rides WITH the id. It was dropped here before, so the only app-wide
          // consumer of this slice (`ScanRoutingProvider`) could not tell a minting session from
          // a filtering one and reported an aim against both. See `ScanSessionReducer`.
          payload: { scanSessionId, scanSessionPurpose: purpose ?? 'certificate' }
        })
      }

      let eventSource: EventSource | null = null;

      if (scanSessionMessageCallback) {
        eventSource = new EventSource(`${axios.defaults.baseURL}/admins/admin/scan-session-listener?id=${scanSessionId}`);
        eventSource.onmessage = ({ data }) => {
          let jsonData: any = {};
          try {
            jsonData = JSON.parse(data);
          } catch (_) {
            jsonData = {};
          }
          // Backward compatibility: if only tagId present, treat as existing.
          //
          // 'removed' MUST be in this list. The fallback below strips everything but `tagId`,
          // so a removal that fell through to it arrived looking exactly like a fresh scan —
          // the modal would have counted the tag it had just been told to drop.
          if (jsonData && (jsonData.status === 'created' || jsonData.status === 'existing' || jsonData.status === 'error' || jsonData.status === 'pending' || jsonData.status === 'removed')) {
            scanSessionMessageCallback(jsonData);
          } else if (jsonData && jsonData.tagId) {
            scanSessionMessageCallback({ tagId: jsonData.tagId });
          } else {
            scanSessionMessageCallback(jsonData);
          }
        };
        eventSource.onerror = (err) => {
          // EventSource auto-reconnects on transient drops; log so a persistently
          // broken stream is visible instead of silently swallowing scans.
          console.error('Scan session SSE error', err)
        };
      }
      if (startScanSessionCallback) {
        // Generate mobile deep link URL instead of web registration URL
        const registerDeviceURL = `${axios.defaults.baseURL}/admins/admin/register-scan-device?id=${scanSessionId}&mobile=true`
        const returnData: IValidationStartScanSessionCallbackParams = {
          success: true,
          scanSessionId: data[SCAN_SESSION_ID_RESPONSE_FIELD],
          registerDeviceURL: registerDeviceURL,
          closeEventSourceFn: eventSource?.close
        }
        startScanSessionCallback(returnData);
      }

      if (scanSessionId && eventSource) {
        activeEventSources[scanSessionId] = eventSource;
      }

    } catch (error) {
      console.log(error);
      if (action.startScanSessionCallback) {
        const returnData: IValidationStartScanSessionCallbackParams = {
          success: false,
          scanSessionId: '',
          registerDeviceURL: ''
        }
        action.startScanSessionCallback(returnData);
      }
    }
  });
}

function* endScanSessionSaga(): Generator {
  yield takeLatest(Actions.END_SCAN_SESSION, function* (action: ActionCallbackOnly) {
    try {
      const scanSessionId: string | null = yield select((state: any) => state.scanSession?.scanSessionId ?? null);

      if (scanSessionId && activeEventSources[scanSessionId]) {
        activeEventSources[scanSessionId].close?.();
        delete activeEventSources[scanSessionId];
      }

      if (scanSessionId) {
        yield call(createApiCall, {
          method: 'POST',
          url: '/admins/admin/toggle-scan-session',
          data: {
            turnOn: false,
          },
        });
      }

      yield put({
        type: Actions.SET_SCAN_SESSION_ID,
        // Clears the purpose too — the reducer reads both off this payload, so a bare `null`
        // would leave the previous session's purpose behind.
        payload: null,
      });

      action.callback?.({ success: true });
    } catch (error) {
      console.log(error);
      action.callback?.({ success: false, content: error });
    }
  });
}

export default [
  fork(startScanSessionSaga),
  fork(endScanSessionSaga)
]
